import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import 'moment/locale/th';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import OnceFooter from './../common/Footer/OnceFooter';
import api from './../../utils/api';
import { numberFormat, transformFieldWithLang } from './../../utils/functions';
import { setPendingPromo } from './../../utils/promoCode';
import './PromoOverview.css';

/**
 * หน้ารายละเอียด voucher / คูปอง ที่ลูกค้าเปิดจาก QR บนรูปที่ CMS ออกให้
 *   voucher  /once/vc/:code/overview
 *   คูปอง    /once/cp/:code/overview
 *
 * สถานะ (ใช้แล้ว / หมดอายุ / ยังไม่เริ่ม / ปิดใช้งาน) มาจากเซิร์ฟเวอร์
 * ดู once-hotel-booking-api/app/service/promo_info.js
 *
 * ปุ่ม "จองเลย" แค่เก็บรหัสไว้ให้หน้าจองหยิบไปใส่เอง (utils/promoCode.js)
 * เงื่อนไขที่ผูกกับวันเข้าพัก/ห้อง ยังตรวจที่หน้าจองเหมือนลูกค้าพิมพ์รหัสเอง
 */

const ENDPOINT = {
  voucher: (code) => `vouchers/items/info/${encodeURIComponent(code)}`,
  coupon: (code) => `coupons/items/info/${encodeURIComponent(code)}`,
};

const WEEKDAY_INDEX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

const fmt = (d) => moment(d).format('DD/MM/YYYY');

/** ช่วงวันที่ที่ด้านใดด้านหนึ่งอาจว่าง -> ข้อความที่เหมาะกับแต่ละกรณี */
const periodText = (t, prefix, start, end) => {
  if (start && end) return t(`${prefix}_range`, { start: fmt(start), end: fmt(end) });
  if (start) return t(`${prefix}_from`, { start: fmt(start) });
  if (end) return t(`${prefix}_until`, { end: fmt(end) });
  return null;
};

export default function PromoOverview({ kind }) {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'th';

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code) return;

    setLoading(true);
    api.request(ENDPOINT[kind](code), (data, suc) => {
      setInfo(suc ? data : null);
      setLoading(false);

      if (suc && data?.promo?.hotel?.code) {
        ReactGA.send({ hitType: 'pageview', page: `${data.promo.hotel.code}/${kind}info`, title: `${kind} info from qr code` });
      }
    });
  }, [code, kind]);

  const kindLabel = t(`promo_kind_${kind}`);
  const toggleLang = () => {
    const next = lang === 'th' ? 'en' : 'th';
    i18n.changeLanguage(next);
    try { localStorage.setItem('trans_lang', next); } catch (e) { /* ไม่เป็นไร */ }
  };

  const weekdayName = (d) => moment().locale(lang === 'th' ? 'th' : 'en').day(WEEKDAY_INDEX[d]).format('dddd');

  if (loading) {
    return (
      <div className="promo-ov">
        <div className="promo-ov__shell promo-ov__shell--empty" aria-busy="true">
          <div className="promo-ov__spinner" />
          <p className="promo-ov__muted">{t('promo_loading')}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <>
        <div className="promo-ov">
          <div className="promo-ov__shell promo-ov__shell--empty">
            <h1 className="promo-ov__empty-title">{t('promo_not_found_title', { kind: kindLabel })}</h1>
            <p className="promo-ov__muted">{t('promo_not_found_hint')}</p>
            <p className="promo-ov__code-plain">{code}</p>
          </div>
        </div>
        <OnceFooter />
      </>
    );
  }

  const { status } = info;
  const hotel = info.promo?.hotel || {};
  // ใบที่ปิดใช้งาน เซิร์ฟเวอร์ส่งมาแค่ข้อมูลที่พัก ไม่มีเงื่อนไข
  const promo = status === 'disabled' ? null : info.promo;
  const hotelName = transformFieldWithLang(hotel, 'name', lang);

  const copyCode = () => {
    try {
      navigator.clipboard.writeText(info.generate_code);
      toast.success(t('promo_copied'));
    } catch (e) { /* เบราว์เซอร์ไม่ให้เขียน clipboard -- รหัสยังเลือกคัดลอกเองได้ */ }
  };

  const bookNow = () => {
    setPendingPromo({ code: info.generate_code, hotel_id: hotel.id, hotel_code: hotel.code });
    navigate(`/${hotel.code}`);
  };

  // ── หัวข้อหลัก: voucher บอกจำนวนคืน / คูปองบอกมูลค่าส่วนลด ─────────────
  let headline = null;
  const subHeadlines = [];

  if (promo && kind === 'voucher') {
    headline = t('promo_nights', { count: Number(promo.day_qty || 1) });
  }

  if (promo && kind === 'coupon') {
    const value = promo.discount_type === 'percent'
      ? `${numberFormat(promo.discount_price, true)}%`
      : `฿${numberFormat(promo.discount_price, true)}`;
    headline = t('promo_discount_off', { value });

    if (promo.discount_type === 'percent' && Number(promo.discount_price_limit) > 0) {
      subHeadlines.push(t('promo_discount_max', { price: numberFormat(promo.discount_price_limit, true) }));
    }
    if (Number(promo.discount_day_limit) > 0) {
      subHeadlines.push(t('promo_discount_day_limit', { count: Number(promo.discount_day_limit) }));
    }
  }

  // ── ห้องที่ใช้ได้ ───────────────────────────────────────────────────
  const roomList = promo?.room_map_type === 'type' ? promo.room_type_items
    : (promo?.room_map_type === 'room' ? promo.room_items : []);

  // ── วันที่เข้าพักได้ ────────────────────────────────────────────────
  let stayDays = null;
  if (promo?.day_for_stay_type === 'all') stayDays = t('promo_days_all');
  if (promo?.day_for_stay_type === 'holiday') stayDays = t('promo_days_holiday');
  if (['day', 'daywithholiday'].includes(promo?.day_for_stay_type) && promo.day_for_stay?.length) {
    const days = promo.day_for_stay.filter((d) => d in WEEKDAY_INDEX).map(weekdayName);
    if (promo.day_for_stay_type === 'daywithholiday') days.push(t('promo_days_holiday'));
    stayDays = days.join(' · ');
  }

  // ── เงื่อนไข ────────────────────────────────────────────────────────
  const conditions = promo ? [
    kind === 'voucher'
      ? periodText(t, 'promo_book_period', promo.use_start_date, promo.use_expire_date)
      : periodText(t, 'promo_book_period', promo.start_date, promo.expire_date),
    kind === 'voucher'
      ? periodText(t, 'promo_stay_period', promo.stay_start_date, promo.stay_enddate_date)
      : periodText(t, 'promo_stay_period', promo.stay_start_date, promo.stay_end_date),
    !!promo.ignore_holiday && t('promo_no_holiday'),
    promo.exclude_day_for_stay?.length > 0 && t('promo_exclude_dates', { dates: promo.exclude_day_for_stay.map(fmt).join(', ') }),
    (kind === 'voucher' && promo.room_map_type === 'all' && Number(promo.price_limit) > 0)
      && t('promo_price_limit', { price: numberFormat(promo.price_limit, true) }),
  ].filter(Boolean) : [];

  // ── สถานะและทางไปต่อ ────────────────────────────────────────────────
  const statusHint = {
    used: info.used_date ? t('promo_used_on', { date: fmt(info.used_date) }) : null,
    expired: t('promo_expired_hint'),
    not_started: t('promo_not_started_hint', {
      date: fmt(kind === 'voucher' ? promo?.use_start_date : promo?.start_date),
    }),
    disabled: t('promo_disabled_hint', { kind: kindLabel }),
  }[status];

  const canBookOnline = status === 'active' && !!hotel.code && !promo?.disabled_booking_fromweb;
  const mustContact = status === 'active' && !!promo?.disabled_booking_fromweb;
  const lineHref = hotel.line_url || (hotel.line_id ? `https://line.me/R/ti/p/${encodeURIComponent(hotel.line_id)}` : null);
  const hasContact = !!hotel.phone_number || !!lineHref;

  return (
    <>
      <div className="promo-ov">
        <div className="promo-ov__topbar">
          <button type="button" className="promo-ov__lang" onClick={toggleLang}>
            {lang === 'th' ? 'EN' : 'TH'}
          </button>
        </div>

        <article className={`promo-ov__shell promo-ov__shell--${status}`}>
          {!!promo?.background_url && (
            <div className="promo-ov__cover" style={{ backgroundImage: `url(${promo.background_url})` }} />
          )}

          <header className="promo-ov__head">
            <div className="promo-ov__hotel">
              {!!hotel.logo_file_path && <img src={hotel.logo_file_path} alt="" className="promo-ov__logo" />}
              {!!hotelName && <span className="promo-ov__hotel-name">{hotelName}</span>}
            </div>
            <span className={`promo-ov__badge promo-ov__badge--${status}`}>{t(`promo_status_${status}`)}</span>
          </header>

          <section className="promo-ov__hero">
            <p className="promo-ov__kind">{kindLabel}</p>
            {!!promo?.name && <h1 className="promo-ov__name">{promo.name}</h1>}
            {/* ชื่อคูปองมักตั้งเป็นมูลค่าส่วนลดอยู่แล้ว -- ไม่ต้องขึ้นซ้ำ */}
            {!!headline && headline !== promo?.name && <p className="promo-ov__headline">{headline}</p>}
            {subHeadlines.map((s) => <p key={s} className="promo-ov__subheadline">{s}</p>)}

            <div className="promo-ov__code">
              <span className="promo-ov__code-label">{t('promo_code_label')}</span>
              <span className="promo-ov__code-value">{info.generate_code}</span>
              <button type="button" className="promo-ov__copy" onClick={copyCode}>{t('promo_copy')}</button>
            </div>

            {/* ไม่แสดงชื่อผู้รับ -- voucher/คูปองถูกส่งต่อให้คนอื่นได้ ชื่อบนหน้านี้จะไม่ตรงกับคนที่ถือ */}
            <p className="promo-ov__reserved">{t('promo_reserved')}</p>
            {!!statusHint && <p className={`promo-ov__status-hint promo-ov__status-hint--${status}`}>{statusHint}</p>}
          </section>

          {!!promo && (
            <section className="promo-ov__section">
              <dl className="promo-ov__facts">
                <dt>{t('promo_rooms_label')}</dt>
                <dd>
                  {promo.room_map_type === 'all' || !roomList?.length
                    ? t('promo_rooms_all')
                    : (
                      <ul className="promo-ov__rooms">
                        {roomList.map((r) => <li key={r.id}>{transformFieldWithLang(r, 'name', lang)}</li>)}
                      </ul>
                    )}
                </dd>

                {!!stayDays && <>
                  <dt>{t('promo_days_label')}</dt>
                  <dd>{stayDays}</dd>
                </>}
              </dl>

              {!!promo.description && <p className="promo-ov__description">{promo.description}</p>}

              {conditions.length > 0 && <>
                <h2 className="promo-ov__section-title">{t('promo_conditions')}</h2>
                <ul className="promo-ov__conditions">
                  {conditions.map((c) => <li key={c}>{c}</li>)}
                </ul>
              </>}
            </section>
          )}

          <footer className="promo-ov__actions">
            {canBookOnline && <>
              <button type="button" className="promo-ov__book" onClick={bookNow}>{t('promo_book_now')}</button>
              <p className="promo-ov__muted promo-ov__book-hint">{t('promo_book_hint')}</p>
            </>}

            {mustContact && <p className="promo-ov__status-hint">{t('promo_web_disabled', { kind: kindLabel })}</p>}

            {(mustContact || status !== 'active') && hasContact && (
              <div className="promo-ov__contact">
                {!!hotel.phone_number && (
                  <a className="promo-ov__contact-btn" href={`tel:${hotel.phone_number}`}>{t('promo_call')} {hotel.phone_number}</a>
                )}
                {!!lineHref && (
                  <a className="promo-ov__contact-btn" href={lineHref} target="_blank" rel="noreferrer">LINE</a>
                )}
              </div>
            )}

            {status !== 'active' && !!hotel.code && (
              <a className="promo-ov__link" href={`/${hotel.code}`}>{t('promo_visit_hotel')}</a>
            )}
          </footer>
        </article>
      </div>
      <OnceFooter />
    </>
  );
}
